/**
 * `/admin/documents` — policy document management. Compliance-admin-only
 * (the server enforces this; a non-admin gets a 403 they can surface
 * inline rather than 404 into the void).
 *
 * The page does two things:
 *   - Lists every document in the register, newest first, with its
 *     active/archived status and a per-row **View PDF** affordance that
 *     opens a short-expiry presigned URL the server mints on demand.
 *     A toggle adds archived versions to the list.
 *   - Inline **Upload new version** disclosure that runs the three-step
 *     admin upload chain — request URL → direct PUT → `POST
 *     /admin/documents` — in a single form. Title, version, and PDF
 *     submit at once; there is no separate publish step.
 *
 * Per-row **Archive** / **Unarchive** buttons retire (or restore) a
 * version. Archive refuses with 409 `document_in_use_by_requirement`
 * when an active requirement still links to the row — operator must
 * repoint the requirement first.
 *
 * 401 anywhere short-circuits to `/admin/sign-in`; other failures surface
 * inline so the admin can retry without losing form state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type { Document } from '@isms/shared';

export interface AdminDocumentsPageProps {
  client: ApiClient;
  /** Where to send the admin on 401. Defaults to `/admin/sign-in`. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; items: Document[] };

type UploadStatus =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'success'; documentId: string };

type ArchiveStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'error'; error: ApiError | string };

type DocumentsView = 'active' | 'archived';

type PdfLinkState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'error'; error: ApiError | string };

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

function toIsoDateTime(value: string | Date): string {
  if (typeof value === 'string') return value.slice(0, 10);
  return value.toISOString().slice(0, 10);
}

export function AdminDocumentsPage({
  client,
  signInPath = '/admin/sign-in',
}: AdminDocumentsPageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [showUpload, setShowUpload] = useState(false);
  const [view, setView] = useState<DocumentsView>('active');
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  // Always fetch with archives included; the tab filters client-side. With a
  // small page_size (50) and few archived rows in practice this is cheaper
  // than re-hitting the API on every tab switch and keeps both tabs warm.
  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const res = await client.adminListDocuments({
        page_size: 50,
        include_archived: true,
      });
      setState({ kind: 'ready', items: res.items });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load the document list. Please check your connection and try again.",
      });
    }
  }, [client, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    if (state.kind !== 'ready') return [];
    return state.items.filter((d) =>
      view === 'active' ? d.archived_at == null : d.archived_at != null,
    );
  }, [state, view]);

  return (
    <main className="admin-documents" aria-labelledby="admin-documents-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin">← Back to dashboard</Link>
      </nav>

      <header className="admin-users-header">
        <h1 id="admin-documents-heading">Policies</h1>
        <p className="admin-users-intro">
          Upload new policy PDFs and manage retired versions. The server hashes the uploaded PDF on
          create; the same hash is recorded on every acknowledgement signature taken against the
          version. To trigger a re-acknowledgement wave, repoint the linked compliance requirement
          to a new version and use <strong>Bulk-invalidate</strong> on the requirement.
        </p>
      </header>

      <section className="admin-users-create" aria-labelledby="admin-documents-upload-heading">
        <header className="admin-users-create-header">
          <h2 id="admin-documents-upload-heading">Upload a new version</h2>
          <button
            type="button"
            className="admin-users-create-toggle"
            onClick={() => setShowUpload((v) => !v)}
            aria-expanded={showUpload}
          >
            {showUpload ? 'Cancel' : 'Upload .pdf'}
          </button>
        </header>
        {showUpload ? (
          <UploadForm
            client={client}
            signInPath={signInPath}
            onSuccess={() => {
              setShowUpload(false);
              void load();
            }}
          />
        ) : (
          <p className="admin-users-create-hint">
            Open the form to upload a PDF policy version. The server hashes the bytes and creates an
            active row in one shot.
          </p>
        )}
      </section>

      <section
        className="admin-item-types-list-section"
        aria-labelledby="admin-documents-list-heading"
      >
        <header className="admin-users-list-header">
          <h2 id="admin-documents-list-heading">
            {view === 'active' ? 'Active documents' : 'Archived documents'}
          </h2>
          <div className="admin-item-types-filter" role="radiogroup" aria-label="Documents view">
            {(['active', 'archived'] as DocumentsView[]).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={view === v}
                className={`admin-people-filter-tab${view === v ? ' is-active' : ''}`}
                onClick={() => setView(v)}
              >
                {v === 'active' ? 'Active' : 'Archived'}
              </button>
            ))}
          </div>
        </header>

        {state.kind === 'loading' ? (
          <p className="admin-people-loading" aria-busy="true">
            Loading documents…
          </p>
        ) : null}

        {state.kind === 'error' ? (
          <div className="admin-people-error">
            <ErrorBanner error={state.error} />
            <button type="button" onClick={() => void load()}>
              Retry
            </button>
          </div>
        ) : null}

        {state.kind === 'ready' && filteredItems.length === 0 ? (
          <p className="admin-people-empty">
            {view === 'active'
              ? 'No active documents. Upload a policy PDF to get started.'
              : 'No archived documents.'}
          </p>
        ) : null}

        {state.kind === 'ready' && filteredItems.length > 0 ? (
          <ul className="admin-documents-list">
            {filteredItems.map((doc) => (
              <DocumentRow
                key={String(doc.document_id)}
                doc={doc}
                client={client}
                signInPath={signInPath}
                onChanged={() => void load()}
              />
            ))}
          </ul>
        ) : null}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Upload form
// ---------------------------------------------------------------------------

interface UploadFormProps {
  client: ApiClient;
  signInPath: string;
  onSuccess: () => void;
}

function UploadForm({ client, signInPath, onSuccess }: UploadFormProps): JSX.Element {
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UploadStatus>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const pending = status.kind === 'uploading';

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    const trimmedTitle = title.trim();
    const trimmedVersion = version.trim();

    if (!trimmedTitle || !trimmedVersion) {
      setStatus({
        kind: 'error',
        error: 'Fill in the title and version before uploading.',
      });
      return;
    }
    if (!file) {
      setStatus({ kind: 'error', error: 'Choose a `.pdf` file to upload.' });
      return;
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setStatus({
        kind: 'error',
        error: 'Only `.pdf` files can be uploaded as policy sources.',
      });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus({
        kind: 'error',
        error: `That file is larger than the ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB limit.`,
      });
      return;
    }

    setStatus({ kind: 'uploading' });
    try {
      const urlResponse = await client.requestAdminDocumentUploadUrl({
        filename: file.name,
        size_bytes: file.size,
      });
      await client.putDocumentFile(urlResponse.upload_url, file);
      const created = await client.adminCreateDocument({
        title: trimmedTitle,
        version: trimmedVersion,
        rendered_pdf_storage_key: urlResponse.storage_key,
      });
      setStatus({ kind: 'success', documentId: String(created.document_id) });
      setTitle('');
      setVersion('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate(signInPath, { replace: true });
        return;
      }
      setStatus({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : err instanceof Error
              ? err.message
              : "We couldn't upload that document. Please try again.",
      });
    }
  }

  return (
    <form className="admin-users-create-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
      <div className="admin-users-form-row">
        <label htmlFor="admin-documents-upload-title">Title</label>
        <input
          id="admin-documents-upload-title"
          type="text"
          autoComplete="off"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={pending}
        />
      </div>
      <div className="admin-users-form-row">
        <label htmlFor="admin-documents-upload-version">Version</label>
        <input
          id="admin-documents-upload-version"
          type="text"
          autoComplete="off"
          required
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          disabled={pending}
        />
        <p className="admin-users-form-hint">
          Combined with the title it must be unique (e.g. <code>2026-04-revised</code>).
        </p>
      </div>
      <div className="admin-users-form-row">
        <label htmlFor="admin-documents-upload-file">Policy PDF</label>
        <input
          id="admin-documents-upload-file"
          type="file"
          accept=".pdf,application/pdf"
          ref={fileInputRef}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={pending}
        />
        <p className="admin-users-form-hint">
          Only `.pdf` is accepted. The server hashes the uploaded bytes on submit.
        </p>
      </div>
      <div className="admin-users-create-actions">
        <button type="submit" className="admin-exports-primary-button" disabled={pending}>
          {pending ? 'Uploading…' : 'Upload and create'}
        </button>
      </div>
      {status.kind === 'error' ? <ErrorBanner error={status.error} /> : null}
      {status.kind === 'success' ? (
        <p className="admin-users-create-success" role="status">
          Uploaded. The new version is visible below.
        </p>
      ) : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Per-row document component
// ---------------------------------------------------------------------------

interface DocumentRowProps {
  doc: Document;
  client: ApiClient;
  signInPath: string;
  onChanged: () => void;
}

function DocumentRow({ doc, client, signInPath, onChanged }: DocumentRowProps): JSX.Element {
  const [archiveStatus, setArchiveStatus] = useState<ArchiveStatus>({ kind: 'idle' });
  const [pdfLink, setPdfLink] = useState<PdfLinkState>({ kind: 'idle' });
  const navigate = useNavigate();

  const isArchived = doc.archived_at != null;

  async function handlePdfLink(): Promise<void> {
    setPdfLink({ kind: 'loading' });
    try {
      const { pdf_url } = await client.getAdminDocumentPdfUrl(String(doc.document_id));
      setPdfLink({ kind: 'ready', url: pdf_url });
      window.open(pdf_url, '_blank', 'noreferrer');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate(signInPath, { replace: true });
        return;
      }
      setPdfLink({
        kind: 'error',
        error: err instanceof ApiError ? err : "We couldn't fetch the PDF link. Please try again.",
      });
    }
  }

  async function handleArchive(): Promise<void> {
    if (
      !window.confirm(
        `Archive "${doc.title}" v${doc.version}? Volunteers will no longer be prompted to acknowledge this version.`,
      )
    )
      return;
    setArchiveStatus({ kind: 'pending' });
    try {
      await client.adminArchiveDocument(String(doc.document_id));
      setArchiveStatus({ kind: 'idle' });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate(signInPath, { replace: true });
        return;
      }
      setArchiveStatus({
        kind: 'error',
        error:
          err instanceof ApiError ? err : "We couldn't archive that version. Please try again.",
      });
    }
  }

  async function handleUnarchive(): Promise<void> {
    setArchiveStatus({ kind: 'pending' });
    try {
      await client.adminUnarchiveDocument(String(doc.document_id));
      setArchiveStatus({ kind: 'idle' });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate(signInPath, { replace: true });
        return;
      }
      setArchiveStatus({
        kind: 'error',
        error:
          err instanceof ApiError ? err : "We couldn't unarchive that version. Please try again.",
      });
    }
  }

  return (
    <li className={`admin-documents-row ${isArchived ? 'is-archived' : 'is-active'}`}>
      <div className="admin-documents-row-header">
        <div className="admin-documents-row-title">
          <h3>{doc.title}</h3>
          <p className="admin-documents-row-meta">
            <code>{doc.version}</code>
          </p>
        </div>
        <span
          className={`admin-people-status ${
            isArchived ? 'admin-documents-status-archived' : 'admin-people-status-active'
          }`}
        >
          {isArchived ? 'Archived' : 'Active'}
        </span>
      </div>

      <dl className="admin-documents-row-details">
        {doc.archived_at ? (
          <>
            <dt>Archived on</dt>
            <dd>{toIsoDateTime(doc.archived_at)}</dd>
          </>
        ) : null}
        <dt>Rendered PDF SHA-256</dt>
        <dd>
          <code className="admin-documents-sha">{doc.rendered_pdf_sha256.slice(0, 16)}…</code>
        </dd>
      </dl>

      <div className="admin-documents-row-actions">
        <button
          type="button"
          className="admin-users-create-toggle"
          onClick={() => void handlePdfLink()}
          disabled={pdfLink.kind === 'loading'}
        >
          {pdfLink.kind === 'loading' ? 'Loading PDF…' : 'View PDF'}
        </button>
        {!isArchived ? (
          <button
            type="button"
            className="admin-users-create-toggle"
            onClick={() => void handleArchive()}
            disabled={archiveStatus.kind === 'pending'}
          >
            {archiveStatus.kind === 'pending' ? 'Archiving…' : 'Archive this version'}
          </button>
        ) : (
          <button
            type="button"
            className="admin-users-create-toggle"
            onClick={() => void handleUnarchive()}
            disabled={archiveStatus.kind === 'pending'}
          >
            {archiveStatus.kind === 'pending' ? 'Unarchiving…' : 'Unarchive'}
          </button>
        )}
      </div>

      {pdfLink.kind === 'error' ? <ErrorBanner error={pdfLink.error} /> : null}

      {archiveStatus.kind === 'error' ? <ErrorBanner error={archiveStatus.error} /> : null}
    </li>
  );
}
